import React, { useState, useEffect } from 'react';
import { getSettings, updateSettings } from '../../services/adminApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'react-toastify';

const SettingsPage = () => {
  const [settings, setSettings] = useState({ coinPercentage: 0, shippingThreshold: 0, shippingCost: 0, leaderboard: { staticLeaders: [] } });
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingShipping, setIsEditingShipping] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const response = await getSettings();
        if (response.success) {
          setSettings(response.settings);
        }
      } catch (error) {
        console.error("Failed to fetch settings", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleUpdate = async () => {
    try {
      const response = await updateSettings(settings);
      if (response.success) {
        setSettings(response.settings);
        setIsEditing(false);
        setIsEditingShipping(false);
        toast.success('Settings updated successfully');
      }
    } catch (error) {
      console.error("Failed to update settings", error);
      toast.error('Failed to update settings');
    }
  };

  const handleAddLeader = () => {
    const newLeaders = [...(settings.leaderboard?.staticLeaders || [])];
    newLeaders.push({
      name: '',
      coins: 0,
      order: newLeaders.length,
    });
    setSettings({
      ...settings,
      leaderboard: {
        staticLeaders: newLeaders,
      },
    });
  };

  const handleRemoveLeader = (index) => {
    const newLeaders = [...(settings.leaderboard?.staticLeaders || [])];
    newLeaders.splice(index, 1);
    setSettings({
      ...settings,
      leaderboard: {
        staticLeaders: newLeaders,
      },
    });
  };

  const handleLeaderChange = (index, field, value) => {
    const newLeaders = [...(settings.leaderboard?.staticLeaders || [])];
    newLeaders[index] = {
      ...newLeaders[index],
      [field]: field === 'coins' || field === 'order' ? parseInt(value) || 0 : value,
    };
    setSettings({
      ...settings,
      leaderboard: {
        staticLeaders: newLeaders,
      },
    });
  };

  const handleChange = (e) => {
    setSettings({ ...settings, [e.target.name]: e.target.value });
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <h1 className="text-2xl font-semibold mb-6">Settings</h1>
      <Card>
        <CardHeader>
          <CardTitle>MAD Coins</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p>Loading settings...</p>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="font-medium">Coin Earning Percentage</label>
                {isEditing ? (
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      name="coinPercentage"
                      value={settings.coinPercentage}
                      onChange={handleChange}
                      className="w-24"
                    />
                    <Button onClick={handleUpdate}>Save</Button>
                    <Button variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span>{settings.coinPercentage}%</span>
                    <Button variant="outline" onClick={() => setIsEditing(true)}>Edit</Button>
                  </div>
                )}
              </div>
              <p className="text-sm text-gray-500">
                This percentage will be applied to all future orders to calculate the amount of MAD Coins earned.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Shipping</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p>Loading settings...</p>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="font-medium">Shipping Cost</label>
                {isEditingShipping ? (
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      name="shippingCost"
                      value={settings.shippingCost}
                      onChange={handleChange}
                      className="w-24"
                    />
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span>£{settings.shippingCost}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between">
                <label className="font-medium">Free Shipping Threshold</label>
                {isEditingShipping ? (
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      name="shippingThreshold"
                      value={settings.shippingThreshold}
                      onChange={handleChange}
                      className="w-24"
                    />
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span>£{settings.shippingThreshold}</span>
                  </div>
                )}
              </div>
              {isEditingShipping ? (
                <div className="flex justify-end gap-2">
                  <Button onClick={handleUpdate}>Save</Button>
                  <Button variant="outline" onClick={() => setIsEditingShipping(false)}>Cancel</Button>
                </div>
              ) : (
                <div className="flex justify-end">
                  <Button variant="outline" onClick={() => setIsEditingShipping(true)}>Edit</Button>
                </div>
              )}
              <p className="text-sm text-gray-500">
                Set the shipping cost and the order total required for free shipping.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Leaderboard - Static Leaders</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p>Loading settings...</p>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-500 mb-4">
                Manage the static leaders shown on the left side of the leaderboard page. These are displayed in the order specified.
              </p>
              {(settings.leaderboard?.staticLeaders || []).map((leader, index) => (
                <div key={index} className="flex items-center gap-4 p-4 border rounded">
                  <Input
                    type="text"
                    placeholder="Leader Name"
                    value={leader.name || ''}
                    onChange={(e) => handleLeaderChange(index, 'name', e.target.value)}
                    className="flex-1"
                  />
                  <Input
                    type="number"
                    placeholder="Coins"
                    value={leader.coins || 0}
                    onChange={(e) => handleLeaderChange(index, 'coins', e.target.value)}
                    className="w-32"
                  />
                  <Input
                    type="number"
                    placeholder="Order"
                    value={leader.order || 0}
                    onChange={(e) => handleLeaderChange(index, 'order', e.target.value)}
                    className="w-24"
                  />
                  <Button
                    variant="outline"
                    onClick={() => handleRemoveLeader(index)}
                    className="text-red-600"
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <div className="flex justify-between items-center">
                <Button variant="outline" onClick={handleAddLeader}>
                  Add Leader
                </Button>
                <Button onClick={handleUpdate}>Save Changes</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsPage;
